/**
 * Lader und Schreibwege des Vertragsbereichs. Jede Funktion nimmt den Anschluss als Argument (testbar gegen die
 * Demo-Datenbank). Rechte prüft die Datenbank: Verträge liest nur das Team (Creator lesen talent_contracts),
 * Status, Nummer, Hashes und Zeitpunkte setzen nur die Vertragsfunktionen.
 */
import { asciiDateiname } from "../../../lib/speicher";
import type { ContractRole, CustomVariableDef, Language, SigningAuthority } from "@talentkontor/domain";
import type { Api } from "../../../data/api";
import { t } from "../../../lib/sprache";
import { musterFuerKonto } from "./baukasten/bibliothek";

export type ContractStatus = "draft" | "sent" | "partially_signed" | "signed" | "void";
export type VoidKind = "withdrawn" | "error" | "mutual" | "superseded";
export type SignerCapacity = "party" | "legal_guardian";

export interface ContractListRow {
  id: string;
  org_id: string;
  deal_id: string | null;
  talent_id: string | null;
  brand_id: string | null;
  template_id: string | null;
  kind_id: string | null;
  contract_number: number | null;
  contract_number_label: string | null;
  title: string;
  language: Language;
  status: ContractStatus;
  origin: "portal" | "pdf_upload";
  sent_at: string | null;
  completed_at: string | null;
  voided_at: string | null;
  void_reason: string | null;
  void_kind: VoidKind | null;
  declined_at: string | null;
  terminated_at: string | null;
  terminated_on: string | null;
  termination_reason: string | null;
  created_at: string;
  updated_at: string;
  required_signers: ContractRole[];
  any_signer_roles: ContractRole[];
  agency_signatures: number;
  visible_to_talent: boolean | null;
  signed_pdf_path: string | null;
  signed_pdf_path_talent: string | null;
  final_sent_to_brand_at: string | null;
  final_sent_to_talent_at: string | null;
  starts_on: string | null;
  ends_on: string | null;
  auto_renew: boolean;
  renewal_months: number | null;
  notice_days: number | null;
  supersedes_id: string | null;
  external_signed_on: string | null;
  /** 0059: Laufzeit in Monaten, Nachtrag zu, Exklusivmanagement, Kündigung durch die Gegenseite */
  term_months: number | null;
  amends_id: string | null;
  exclusive_management: boolean;
  exclusive_scope: string | null;
  tail_months: number | null;
  termination_received_on: string | null;
  termination_message: string | null;
  /** 0071: Vorlage oder Dokument der Marke (PDF) */
  document_kind?: "template" | "brand_pdf";
  /** 0325: Beispieldaten des Schnellstarts (Etikett „Beispiel“ in der Liste) */
  is_sample?: boolean;
}
export interface ContractRow extends ContractListRow {
  body_html: string;
  annex_html: string | null;
  rendered_html: string | null;
  rendered_annex_html: string | null;
  variable_defs: CustomVariableDef[];
  variables: Record<string, unknown>;
  annex_hidden_from: ContractRole[];
  mask_agency_address_until_complete: boolean;
  template_version: number | null;
  document_sha256: string | null;
  final_sha256: string | null;
  void_document_path: string | null;
  created_by: string | null;
  /** 0071: PDF der Marke (Pfad, Prüfwert, Name, Größe) und gewählte Vertretung der Agentur */
  source_pdf_path?: string | null;
  source_pdf_sha256?: string | null;
  source_pdf_name?: string | null;
  source_pdf_size?: number | null;
  agency_representatives?: { name: string; position?: string | null }[] | null;
  /** 0112 (VB-22): Text weicht von der Vorlagenfassung ab; null bei älteren und extern abgelegten Verträgen */
  text_differs_from_template?: boolean | null;
}
export interface SignerRow {
  id: string;
  contract_id: string;
  role: ContractRole;
  slot: number;
  capacity: SignerCapacity;
  name: string;
  email: string;
  ui_language: Language | null;
  user_id: string | null;
  token_expires_at: string | null;
  signed_at: string | null;
  signing_name: string | null;
  signing_position: string | null;
  signing_authority: SigningAuthority | null;
  filled_variables: Record<string, string>;
  signature_data: string | null;
  sent_at: string | null;
  viewed_at: string | null;
  used_at: string | null;
  invalidated_at: string | null;
  decline_reason: string | null;
  read_token_expires_at: string | null;
  reminded_at: string | null;
  /** 0059: auf Papier unterschrieben (Datum auf dem Papier, Scan als Anhang des Vertrags) */
  paper_signed_on: string | null;
  paper_document_path: string | null;
}
export interface EventRow {
  id: number;
  contract_id: string;
  signer_id: string | null;
  event: string;
  at: string;
  ip: string | null;
  user_agent: string | null;
  document_sha256: string | null;
  view_sha256: string | null;
  /** Prüfwert des Hauptteils zum Zeitpunkt des Ereignisses (0117); Bestand null */
  main_sha256?: string | null;
  actor_user_id: string | null;
  client_info: Record<string, string> | null;
  details: Record<string, unknown> | null;
}
export interface KindRow {
  id: string;
  key: string;
  name_de: string;
  name_en: string;
  sort_order: number;
  /** 0059: archivierte Vertragsarten stehen in keiner Auswahl mehr */
  archived_at?: string | null;
}
export interface TemplateRow {
  id: string;
  org_id: string;
  kind_id: string | null;
  key: string;
  name: string;
  language: Language;
  version: number;
  parent_id: string | null;
  status: "draft" | "active" | "archived";
  description: string | null;
  body_html: string;
  annex_html: string | null;
  annex_hidden_from: ContractRole[];
  variables: CustomVariableDef[];
  required_signers: ContractRole[];
  mask_agency_address_until_complete: boolean;
  platform_version: number | null;
  /** Bausteine (0112), null = Vorlage aus dem Bestand */
  blocks?: import("@talentkontor/domain").Block[] | null;
  blocks_schema?: number | null;
  created_at: string;
  updated_at: string;
}
export interface PlatformTemplateRow {
  key: string;
  kind_key: string;
  kind_name_de: string;
  kind_name_en: string;
  language: Language;
  name: string;
  version: number;
  description: string | null;
  required_signers: ContractRole[];
}
export interface ContractSettings {
  org_id: string;
  number_format: string;
  governing_law_en: string | null;
  jurisdiction_en: string | null;
  representative_position_en: string | null;
  read_link_days: number;
  signer_reminder_days: number;
  deadline_reminder_days: number;
  privacy_url: string | null;
  imprint_url: string | null;
  /** 0059: Anrede der Creator auf der Signierseite (Befund 29) */
  creator_address_form: "du" | "sie";
}

export const LIST_COLUMNS = "id, org_id, deal_id, talent_id, brand_id, template_id, kind_id, contract_number, contract_number_label, title, language, status, origin, sent_at, completed_at, voided_at, void_reason, void_kind, declined_at, terminated_at, terminated_on, termination_reason, created_at, updated_at, required_signers, any_signer_roles, agency_signatures, visible_to_talent, signed_pdf_path, signed_pdf_path_talent, final_sent_to_brand_at, final_sent_to_talent_at, starts_on, ends_on, auto_renew, renewal_months, notice_days, supersedes_id, external_signed_on, term_months, amends_id, exclusive_management, exclusive_scope, tail_months, termination_received_on, termination_message, document_kind, is_sample";
const SIGNER_COLUMNS = "id, contract_id, role, slot, capacity, name, email, ui_language, user_id, token_expires_at, signed_at, signing_name, signing_position, signing_authority, filled_variables, signature_data, sent_at, viewed_at, used_at, invalidated_at, decline_reason, read_token_expires_at, reminded_at, paper_signed_on, paper_document_path";
const TEMPLATE_LIST_COLUMNS = "id, org_id, kind_id, key, name, language, version, parent_id, status, description, required_signers, annex_hidden_from, platform_version, blocks_schema, created_at, updated_at";

const byOrg = (orgId: string) => [["org_id", "eq", orgId]] as const;

export function defaultContractSettings(orgId: string): ContractSettings {
  return { org_id: orgId, number_format: "V-{NNNN}", governing_law_en: null, jurisdiction_en: null, representative_position_en: null,
    read_link_days: 90, signer_reminder_days: 3, deadline_reminder_days: 30, privacy_url: null, imprint_url: null, creator_address_form: "du" };
}

export async function loadContractSettings(api: Api, orgId: string): Promise<ContractSettings> {
  const rows = await api.select<ContractSettings>("contract_settings", { filters: byOrg(orgId) }).catch(() => [] as ContractSettings[]);
  return rows[0] ?? defaultContractSettings(orgId);
}

export interface VertragsListe {
  contracts: ContractListRow[];
  signers: SignerRow[];
  kinds: KindRow[];
  /** Creator mit Portalzugang: ihr Link läuft nicht „ab“, sie unterschreiben im Portal (Befund 27) */
  portalTalents: Set<string>;
}

export async function loadVertragsListe(api: Api, orgId: string): Promise<VertragsListe> {
  const [contracts, signers, kinds, links] = await Promise.all([
    api.select<ContractListRow>("contracts", { columns: LIST_COLUMNS, filters: byOrg(orgId), order: [["created_at", "desc"]] }),
    api.select<SignerRow>("contract_signers", { columns: SIGNER_COLUMNS, filters: byOrg(orgId) }),
    api.select<KindRow>("contract_kinds", { columns: "id, key, name_de, name_en, sort_order, archived_at", filters: byOrg(orgId), order: [["sort_order", "asc"], ["name_de", "asc"]] }),
    api.select<{ talent_id: string }>("talent_user_links", { columns: "talent_id", filters: byOrg(orgId) }).catch(() => [] as { talent_id: string }[]),
  ]);
  return { contracts, signers, kinds, portalTalents: new Set(links.map((l) => l.talent_id)) };
}

export interface OrgDaten {
  settings: Record<string, unknown> & { legal_name: string | null; street: string | null; postal_code: string | null; city: string | null; country: string | null;
    vat_id: string | null; tax_number: string | null; reply_to_email: string | null; default_commission_bp: number | null; locale: string; currency: string;
    signing_link_days: number | null; supported_languages: string[] | null; default_document_language: string | null;
    invoice_route: string | null; payment_terms_days: number | null; talent_payment_terms_days: number | null; brand_payment_terms_days: number | null };
  legal: { representative_name: string | null; representative_position: string | null; register_type: string | null; register_number: string | null;
    register_court: string | null; phone: string | null; website: string | null; governing_law: string | null; jurisdiction: string | null;
    /** 0071: alle Vertretungsberechtigten, Gesamtvertretung */
    representatives?: { name: string; position?: string | null }[] | null; joint_representation?: boolean } | null;
  signingLinkDays: number;
  contractSettings: ContractSettings;
}

export async function loadOrgDaten(api: Api, orgId: string): Promise<OrgDaten> {
  const [settings, legal, defaults, contractSettings] = await Promise.all([
    api.select<OrgDaten["settings"]>("org_settings", { filters: byOrg(orgId) }),
    api.select<NonNullable<OrgDaten["legal"]>>("org_legal", { filters: byOrg(orgId) }),
    api.select<{ key: string; value: unknown }>("platform_defaults", { filters: [["key", "in", ["signing_link_days", "free_revisions"]]] }).catch(() => []),
    loadContractSettings(api, orgId),
  ]);
  const s = settings[0];
  if (!s) throw new Error(t("Die Einstellungen der Agentur fehlen.", "The agency's settings are missing."));
  const fallback = Number(defaults.find((d) => d.key === "signing_link_days")?.value ?? 14);
  // Nachtest CO-009: enthaltene Korrekturschleifen wie die Kooperationen (Einstellung, sonst Produkt-Standard, sonst 2)
  const schleifen = Number(defaults.find((d) => d.key === "free_revisions")?.value ?? 2);
  const free_revisions = (s["free_revisions"] as number | null | undefined) ?? (Number.isInteger(schleifen) ? schleifen : 2);
  return { settings: { ...s, free_revisions }, legal: legal[0] ?? null, signingLinkDays: s.signing_link_days ?? (Number.isFinite(fallback) ? fallback : 14), contractSettings };
}

export interface TalentFull {
  id: string; first_name: string; last_name: string | null; display_name: string | null; email: string | null; street: string | null;
  postal_code: string | null; city: string | null; country: string | null; vat_id: string | null; tax_number: string | null;
  small_business: boolean | null; instagram_handle: string | null; commission_bp: number | null; status: string;
  company_name?: string | null; birth_date?: string | null; guardian_name?: string | null; guardian_email?: string | null;
  guardian2_name?: string | null; guardian2_email?: string | null;
}
export interface BrandFull {
  id: string; name: string; legal_name: string | null; street: string | null; postal_code: string | null; city: string | null;
  country: string | null; vat_id: string | null; language: string | null; invoice_email: string | null; register_info: string | null;
  represented_by: string | null; payment_terms_days: number | null; invoice_route?: string | null;
}
export interface ContactRow { id: string; brand_id: string; name: string | null; email: string | null; role: string | null; is_invoice_recipient: boolean; signs_contracts: boolean }
export interface DealFull {
  id: string; talent_id: string; brand_id: string; title: string; status: string; model: string; budget_cents: number | null; currency: string;
  commission_bp: number | null; start_month: string | null; months: number | null; deliverables: string | null; platforms: string[] | null;
  usage_rights: string | null; usage_rights_until: string | null; content_deadline: string | null; archived_at: string | null; exclusive: boolean | null;
  exclusive_until?: string | null; exclusive_scope?: string | null; invoice_route?: string | null;
  free_revisions?: number | null; revision_fee_cents?: number | null;
}
export interface ItemRow { id: string; deal_id: string; kind: string; description: string; amount_cents: number; commissionable: boolean; period_id: string | null }
export interface PeriodRow { id: string; deal_id: string; month: string; amount_cents: number | null; status: string }

export interface VertragDetail {
  contract: ContractRow;
  signers: SignerRow[];
  events: EventRow[];
  kind: KindRow | null;
  talent: TalentFull | null;
  brand: BrandFull | null;
  /** Agentur der Marke (brands.represented_by), falls die Marke vertreten wird */
  representedBy: string | null;
  contacts: ContactRow[];
  /** Kontakte der Agentur, die die Marke vertritt (Unterzeichner-Vorschläge, Praxis) */
  agencyContacts: ContactRow[];
  deal: DealFull | null;
  items: ItemRow[];
  periods: PeriodRow[];
  /** Beiträge der Kooperation und der Plattform-Katalog (0068) für {{deal.deliverables_list}} */
  beitraege?: BeitragQuelleRow[];
  plattformen?: PlattformRow[];
  terms: { agency_fee_mode: string | null; agency_fee_bp: number | null; agency_fee_cents: number | null } | null;
  members: MemberRow[];
  talentHasPortal: boolean;
  /** ersetzte bzw. ersetzende Fassung */
  supersedes: ContractRef | null;
  successor: ContractRef | null;
  /** Nachtrag: ergänzter Ursprungsvertrag; am Ursprungsvertrag seine Nachträge (Praxis) */
  original: ContractRef | null;
  amendments: ContractRef[];
  /** Anhänge des Vertrags (Kündigungsschreiben, Scans von Papierunterschriften) */
  attachments: AttachmentRow[];
}
export interface ContractRef {
  id: string; title: string; contract_number_label: string | null; status: ContractStatus; completed_at?: string | null;
  /** Nachtrag: Tag des Vertragsschlusses des Ursprungsvertrags aus der Datenbank (public.contract_original_day, 0124, Runde 3 R3-04) */
  concluded_on?: string | null;
  talent_id?: string | null; brand_id?: string | null; starts_on?: string | null; void_kind?: VoidKind | null; terminated_on?: string | null;
}
export interface MemberRow {
  user_id: string; display_name: string | null; job_title: string | null; contract_signing_name: string | null; contract_signing_position: string | null;
  contract_signing_authority: SigningAuthority | null; role?: string; active?: boolean; can_sign_contracts?: boolean; sees_all_talents?: boolean;
}
export interface AttachmentRow { id: string; storage_path: string; file_name: string; created_at: string }

/** Beitrag (0068) mit den Angaben, die der Vertragstext braucht */
export interface BeitragQuelleRow {
  id: string; status: string; format: string; format_label: string | null; platform: string; title: string | null;
  draft_due: string | null; publish_on: string | null; published_on: string | null; usage_rights_months: number | null;
  usage_kinds?: string[] | null; usage_exclusive?: boolean | null; usage_territory?: string | null;
}
export interface PlattformRow { key: string; label_de: string; label_en: string | null }

/** Stammdaten einer Kooperation für den Vertragskontext (Positionen, Monate, Beiträge mit Plattform-Katalog). */
export async function loadDealExtras(api: Api, dealId: string | null): Promise<{ deal: DealFull | null; items: ItemRow[]; periods: PeriodRow[]; beitraege: BeitragQuelleRow[]; plattformen: PlattformRow[] }> {
  if (!dealId) return { deal: null, items: [], periods: [], beitraege: [], plattformen: [] };
  const [deals, items, periods, beitraege] = await Promise.all([
    api.select<DealFull>("deals", { filters: [["id", "eq", dealId]] }),
    // Reihenfolge fest wie in app.contract_render_context (Runde 2, R2-01: der Server rendert denselben Text nach)
    api.select<ItemRow>("deal_line_items", { filters: [["deal_id", "eq", dealId]], order: [["created_at", "asc"], ["id", "asc"]] }).catch(() => [] as ItemRow[]),
    api.select<PeriodRow>("deal_periods", { columns: "id, deal_id, month, amount_cents, status", filters: [["deal_id", "eq", dealId]], order: [["month", "asc"], ["id", "asc"]] }).catch(() => [] as PeriodRow[]),
    api.select<BeitragQuelleRow>("deal_deliverables", {
      columns: "id, status, format, format_label, platform, title, draft_due, publish_on, published_on, usage_rights_months, usage_kinds, usage_exclusive, usage_territory",
      filters: [["deal_id", "eq", dealId]], order: [["sort_order", "asc"], ["created_at", "asc"], ["id", "asc"]],
    }).catch(() => [] as BeitragQuelleRow[]),
  ]);
  const deal = deals[0] ?? null;
  // Katalog der Agentur der Kooperation (die Zeile kommt mit allen Spalten)
  const orgId = (deal as (DealFull & { org_id?: string }) | null)?.org_id;
  const plattformen = orgId && beitraege.length
    ? await api.select<PlattformRow>("org_deal_platforms", { columns: "key, label_de, label_en", filters: [["org_id", "eq", orgId]], order: [["sort_order", "asc"], ["created_at", "asc"], ["id", "asc"]] }).catch(() => [] as PlattformRow[])
    : [];
  return { deal, items, periods, beitraege, plattformen };
}

export async function loadBrandExtras(api: Api, brandId: string | null): Promise<{ brand: BrandFull | null; contacts: ContactRow[]; representedBy: string | null; agencyContacts: ContactRow[] }> {
  if (!brandId) return { brand: null, contacts: [], representedBy: null, agencyContacts: [] };
  const [brands, contacts] = await Promise.all([
    api.select<BrandFull>("brands", { filters: [["id", "eq", brandId]] }),
    api.select<ContactRow>("brand_contacts", { filters: [["brand_id", "eq", brandId]], order: [["created_at", "asc"], ["id", "asc"]] }).catch(() => [] as ContactRow[]),
  ]);
  const brand = brands[0] ?? null;
  // Vertritt eine Agentur die Marke, schlägt der Vertrag auch deren Kontakte als Unterzeichner vor (Praxis)
  const [rep, agencyContacts] = brand?.represented_by ? await Promise.all([
    api.select<{ name: string; legal_name: string | null }>("brands", { columns: "name, legal_name", filters: [["id", "eq", brand.represented_by]] }).then((r) => r[0] ?? null),
    api.select<ContactRow>("brand_contacts", { filters: [["brand_id", "eq", brand.represented_by]], order: [["created_at", "asc"]] }).catch(() => [] as ContactRow[]),
  ]) : [null, [] as ContactRow[]] as const;
  return { brand, contacts, representedBy: rep ? rep.legal_name ?? rep.name : null, agencyContacts: [...agencyContacts] };
}

/** Tag des Vertragsschlusses eines Ursprungsvertrags, wie ihn der Server im Nachtrag rendert (0124, Runde 3 R3-04); null, solange offen */
export const loadOriginalDay = (api: Api, id: string): Promise<string | null> =>
  api.read<string | null>("contract_original_day", { p_contract: id }).then((d) => d ?? null).catch(() => null);

export async function loadVertrag(api: Api, orgId: string, id: string): Promise<VertragDetail | null> {
  const contract = (await api.select<ContractRow>("contracts", { filters: [["id", "eq", id], ["org_id", "eq", orgId]] }))[0];
  if (!contract) return null;
  const REF = "id, title, contract_number_label, status, completed_at, talent_id, brand_id, starts_on, void_kind, terminated_on";
  const [signers, events, kinds, talents, brand, dealX, terms, members, portal, older, newer, original, amendments, attachments] = await Promise.all([
    api.select<SignerRow>("contract_signers", { columns: SIGNER_COLUMNS, filters: [["contract_id", "eq", id]], order: [["slot", "asc"]] }),
    api.select<EventRow>("signature_events", { filters: [["contract_id", "eq", id]], order: [["at", "asc"], ["id", "asc"]] }).catch(() => [] as EventRow[]),
    contract.kind_id ? api.select<KindRow>("contract_kinds", { filters: [["id", "eq", contract.kind_id]] }) : Promise.resolve([]),
    contract.talent_id ? api.select<TalentFull>("talents", { filters: [["id", "eq", contract.talent_id]] }) : Promise.resolve([]),
    loadBrandExtras(api, contract.brand_id),
    loadDealExtras(api, contract.deal_id),
    contract.deal_id ? api.select<VertragDetail["terms"] & object>("deal_agency_terms", { filters: [["deal_id", "eq", contract.deal_id]] }).catch(() => []) : Promise.resolve([]),
    api.select<MemberRow>("memberships", { columns: "user_id, display_name, job_title, contract_signing_name, contract_signing_position, contract_signing_authority, role, active, can_sign_contracts, sees_all_talents", filters: byOrg(orgId) }).catch(() => [] as MemberRow[]),
    contract.talent_id ? api.select<{ talent_id: string }>("talent_user_links", { columns: "talent_id", filters: [["talent_id", "eq", contract.talent_id]] }).catch(() => []) : Promise.resolve([]),
    contract.supersedes_id ? api.select<ContractRef>("contracts", { columns: REF, filters: [["id", "eq", contract.supersedes_id]] }).catch(() => [] as ContractRef[]) : Promise.resolve([] as ContractRef[]),
    api.select<ContractRef>("contracts", { columns: REF, filters: [["supersedes_id", "eq", id]], order: [["created_at", "desc"]], limit: 1 }).catch(() => [] as ContractRef[]),
    // Runde 3, R3-04: das Datum des Ursprungsvertrags bestimmt die Datenbank (Tag des Vertragsschlusses, wie der Server rendert)
    contract.amends_id ? Promise.all([
      api.select<ContractRef>("contracts", { columns: REF, filters: [["id", "eq", contract.amends_id]] }).catch(() => [] as ContractRef[]),
      loadOriginalDay(api, contract.amends_id),
    ]).then(([rows, day]) => rows.map((r) => ({ ...r, concluded_on: day ?? null }))) : Promise.resolve([] as ContractRef[]),
    api.select<ContractRef>("contracts", { columns: REF, filters: [["amends_id", "eq", id]], order: [["created_at", "asc"]] }).catch(() => [] as ContractRef[]),
    api.select<AttachmentRow>("attachments", { columns: "id, storage_path, file_name, created_at", filters: [["owner_kind", "eq", "contract"], ["owner_id", "eq", id], ["deleted_at", "is", null]], order: [["created_at", "asc"]] }).catch(() => [] as AttachmentRow[]),
  ]);
  return {
    contract, signers, events, kind: kinds[0] ?? null, talent: talents[0] ?? null, brand: brand.brand, representedBy: brand.representedBy,
    contacts: brand.contacts, agencyContacts: brand.agencyContacts, deal: dealX.deal, items: dealX.items, periods: dealX.periods,
    beitraege: dealX.beitraege, plattformen: dealX.plattformen, terms: terms[0] ?? null, members,
    talentHasPortal: portal.length > 0, supersedes: older[0] ?? null, successor: newer[0] ?? null, original: original[0] ?? null, amendments, attachments,
  };
}

export async function loadVorlagen(api: Api, orgId: string): Promise<{ templates: TemplateRow[]; kinds: KindRow[] }> {
  const [templates, kinds] = await Promise.all([
    api.select<TemplateRow>("contract_templates", { columns: TEMPLATE_LIST_COLUMNS, filters: byOrg(orgId), order: [["name", "asc"], ["version", "desc"]] }),
    api.select<KindRow>("contract_kinds", { columns: "id, key, name_de, name_en, sort_order, archived_at", filters: byOrg(orgId), order: [["sort_order", "asc"], ["name_de", "asc"]] }),
  ]);
  return { templates, kinds };
}

export async function loadPlatformTemplates(api: Api): Promise<PlatformTemplateRow[]> {
  return api.select<PlatformTemplateRow>("platform_contract_templates", {
    columns: "key, kind_key, kind_name_de, kind_name_en, language, name, version, description, required_signers", filters: [musterFuerKonto()], order: [["key", "asc"], ["language", "asc"]],
  });
}

export async function loadVorlage(api: Api, orgId: string, id: string): Promise<TemplateRow | null> {
  return (await api.select<TemplateRow>("contract_templates", { filters: [["id", "eq", id], ["org_id", "eq", orgId]] }))[0] ?? null;
}

// ── Schreiben ───────────────────────────────────────────────────────────────────

export interface EntwurfDaten {
  title: string;
  language: Language;
  body_html: string;
  annex_html: string | null;
  rendered_html: string;
  rendered_annex_html: string | null;
  variable_defs: CustomVariableDef[];
  variables: Record<string, unknown>;
  required_signers: ContractRole[];
  annex_hidden_from: ContractRole[];
  any_signer_roles: ContractRole[];
  agency_signatures: number;
  visible_to_talent: boolean | null;
  starts_on: string | null;
  ends_on: string | null;
  auto_renew: boolean;
  renewal_months: number | null;
  notice_days: number | null;
  deal_id: string | null;
  talent_id: string | null;
  brand_id: string | null;
  kind_id: string | null;
  term_months: number | null;
  exclusive_management: boolean;
  exclusive_scope: string | null;
  tail_months: number | null;
  amends_id: string | null;
  /** 0071: gewählte Vertretung der Agentur (null: die ersten der Firmenangaben) */
  agency_representatives: { name: string; position?: string | null }[] | null;
}

export async function createDraft(api: Api, orgId: string, d: Partial<EntwurfDaten> & { template_id: string | null; template_version: number | null }): Promise<string> {
  const [row] = await api.insert<{ id: string }>("contracts", { org_id: orgId, ...d });
  if (!row) throw new Error(t("Der Vertrag wurde nicht angelegt.", "The contract was not created."));
  return row.id;
}

export async function saveDraft(api: Api, id: string, d: Partial<EntwurfDaten>): Promise<void> {
  await api.update("contracts", [["id", "eq", id]], d as Record<string, unknown>, { columns: "id" });
}

export interface SignerInput {
  id?: string;
  role: ContractRole;
  slot: number;
  capacity: SignerCapacity;
  name: string;
  email: string;
  ui_language: Language | null;
  signing_position?: string | null;
}

/** Unterzeichner eines Entwurfs abgleichen: je Partei eine Liste (Platz 1 bis 6); die Agentur legt die Datenbank beim Versand an. */
export async function syncSigners(api: Api, orgId: string, contractId: string, existing: readonly SignerRow[], wanted: readonly SignerInput[]): Promise<void> {
  const rows = wanted.filter((w) => w.role !== "agency" && (w.name.trim() || w.email.trim()));
  // Plätze je Partei neu durchzählen (Lücken nach dem Entfernen schließen)
  const slots = new Map<ContractRole, number>();
  const plan = rows.map((w) => {
    const slot = (slots.get(w.role) ?? 0) + 1;
    slots.set(w.role, slot);
    return { ...w, slot, name: w.name.trim(), email: w.email.trim().toLowerCase(), signing_position: w.signing_position?.trim() || null };
  });
  // Zuerst entfernen, was es nicht mehr gibt; danach rücken die übrigen je Partei in aufsteigender Reihenfolge nach
  // (der Zielplatz ist dann immer frei, die Eindeutigkeit je Partei und Platz bleibt gewahrt)
  for (const row of existing) {
    if (row.role === "agency") continue;
    if (!plan.some((p) => p.id === row.id)) await api.remove("contract_signers", [["id", "eq", row.id]]);
  }
  for (const p of plan) {
    const patch = { role: p.role, slot: p.slot, capacity: p.capacity, name: p.name, email: p.email, ui_language: p.ui_language, signing_position: p.signing_position };
    if (p.id && existing.some((e) => e.id === p.id)) {
      await api.update("contract_signers", [["id", "eq", p.id]], patch, { columns: "id" });
    } else {
      // Unterzeichner-Zeilen tragen Link-Schlüssel, die niemand lesen darf: nur die Kennung zurückholen
      await api.insert("contract_signers", { org_id: orgId, contract_id: contractId, ...patch }, { columns: "id" });
    }
  }
}

export interface SendLink { signer_id: string; role: ContractRole; slot: number; capacity: SignerCapacity; email: string; name: string; token: string | null; expires_at: string }
export interface SendResult { contract_id: string; links: SendLink[]; tokens_hidden: boolean; contract_number_label: string | null }

export const reserveNumber = (api: Api, id: string) => api.rpc<string | null>("contract_reserve_number", { p_contract: id });
export const sendContract = (api: Api, id: string, message: string | null) =>
  api.rpc<SendResult>("contract_send", { p_contract: id, p_message: message?.trim() || null });
export const resendContract = (api: Api, id: string, role: ContractRole, message: string | null) =>
  api.rpc<{ role: ContractRole; email: string; token: string | null; expires_at: string }>("contract_resend", { p_contract: id, p_role: role, p_message: message?.trim() || null });
export const resendSigner = (api: Api, signerId: string, message: string | null, name?: string | null, email?: string | null) =>
  api.rpc<{ signer_id: string; role: ContractRole; name: string; email: string; token: string | null; expires_at: string }>("contract_resend_signer", {
    p_signer: signerId, p_message: message?.trim() || null, p_name: name?.trim() || null, p_email: email?.trim() || null });
export const shareLink = (api: Api, signerId: string) =>
  api.rpc<{ signer_id: string; role: ContractRole; name: string; email: string; token: string; expires_at: string }>("contract_share_link", { p_signer: signerId });
export const sendCopy = (api: Api, signerId: string) =>
  api.rpc<{ email: string; read_until: string; token: string | null }>("contract_send_copy", { p_signer: signerId });
export const voidContract = (api: Api, id: string, reason: string, kind: VoidKind | null = null, documentPath: string | null = null) =>
  api.rpc("contract_void", { p_contract: id, p_reason: reason, p_kind: kind, p_document_path: documentPath });
/** Kündigung: interne Notiz (reason) getrennt von der Mitteilung an die Parteien (message), Befund 11. */
export const terminateContract = (api: Api, id: string, effective: string, reason: string, by: ContractRole,
  more: { message?: string | null; receivedOn?: string | null; documentPath?: string | null; extraordinary?: boolean } = {}) =>
  api.rpc("contract_terminate", { p_contract: id, p_effective: effective, p_reason: reason, p_by: by, p_message: more.message?.trim() || null,
    p_received_on: more.receivedOn || null, p_document_path: more.documentPath ?? null, p_extraordinary: !!more.extraordinary });
/** Kündigungsvermerk berichtigen (VERTRAEGE-R2-02): neues Enddatum und Zugang mit Grund; ein geändertes Ende erfahren die Parteien. */
export const correctTermination = (api: Api, id: string, effective: string, reason: string,
  more: { receivedOn?: string | null; extraordinary?: boolean; message?: string | null } = {}) =>
  api.rpc("contract_correct_termination", { p_contract: id, p_effective: effective, p_received_on: more.receivedOn || null, p_reason: reason,
    p_extraordinary: !!more.extraordinary, p_message: more.message?.trim() || null });
export const setTerm = (api: Api, id: string, t: { starts_on: string | null; ends_on: string | null; auto_renew: boolean; renewal_months: number | null; notice_days: number | null }) =>
  api.rpc("contract_set_term", { p_contract: id, p_starts_on: t.starts_on, p_ends_on: t.ends_on, p_auto_renew: t.auto_renew, p_renewal_months: t.renewal_months, p_notice_days: t.notice_days });
export const setVisibility = (api: Api, id: string, visible: boolean | null) => api.rpc("contract_set_visibility", { p_contract: id, p_visible: visible });
/** replace = false: Kopie ohne Vorgänger (ersetzt nie etwas); true: neue Fassung derselben Parteien (Befund 1). */
export const copyAsDraft = (api: Api, id: string, replace: boolean) => api.rpc<string>("contract_copy_as_draft", { p_contract: id, p_replace: replace });
export const detachVersion = (api: Api, id: string) => api.rpc("contract_detach_version", { p_contract: id });
export const linkDeal = (api: Api, id: string, dealId: string | null, reason?: string | null) =>
  api.rpc("contract_link_deal", { p_contract: id, p_deal: dealId, p_reason: reason?.trim() || null });
export const markFinalSent = (api: Api, id: string, role: "brand" | "talent") => api.rpc("contract_mark_final_sent", { p_contract: id, p_role: role });
export const deleteDraft = (api: Api, id: string) => api.remove("contracts", [["id", "eq", id]]);
export const saveContractSettings = (api: Api, orgId: string, settings: Partial<Omit<ContractSettings, "org_id">>) =>
  api.rpc("contract_settings_save", { p_org: orgId, p_settings: settings });
/** replaceDraft (K-401): ein offener Entwurf derselben Vorlage weicht der neuen Fassung */
export const importPlatformTemplate = (api: Api, orgId: string, key: string, language: Language, replaceDraft = false) =>
  api.rpc<string>("contract_template_import_platform", { p_org: orgId, p_key: key, p_language: language, ...(replaceDraft ? { p_replace_draft: true } : {}) });

/** Dateiname für den Speicher: nur ASCII-Buchstaben, Ziffern, Punkt, Minus und Unterstrich (Umlaute umschrieben). */
export function safeFileName(name: string, fallback = "vertrag.pdf"): string {
  return asciiDateiname(name, fallback);
}

/** Fertiges PDF eines Portalvertrags ablegen (optional mit Creator-Fassung ohne Anlage). */
export async function uploadFinalPdf(api: Api, orgId: string, id: string, file: Blob, fileName: string, talentFile?: Blob | null): Promise<void> {
  const stamp = Date.now();
  const path = `${orgId}/contract/${id}/${stamp}-${safeFileName(fileName)}`;
  await api.upload("documents", path, file);
  let talentPath: string | null = null;
  if (talentFile) {
    talentPath = `${orgId}/contract/${id}/${stamp}-creator-${safeFileName(fileName)}`;
    await api.upload("documents", talentPath, talentFile);
  }
  await api.rpc("contract_upload_final_pdf", { p_contract: id, p_path: path, p_path_talent: talentPath });
}

/** Angaben zum Gerät für die Beweiskette (Zeitzone, Sprache, Bildschirm); IP und Browser kennt nur der Server. */
export function clientInfo(): Record<string, string> {
  try {
    return {
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      language: navigator.language,
      screen: `${window.screen.width}x${window.screen.height}`,
      platform: (navigator as Navigator & { userAgentData?: { platform?: string } }).userAgentData?.platform ?? "",
    };
  } catch {
    return {};
  }
}

/**
 * Gegenzeichnen (Befund 12, 13): ein vom Konto abweichender Name nur mit confirmName (steht in der Beweiskette); vor den anderen
 * Parteien nur mit early.
 */
export const countersign = (api: Api, id: string, signatureData: string | null,
  who: { name?: string | null; position?: string | null; authority?: SigningAuthority | null; confirmName?: boolean; early?: boolean } = {}) =>
  api.rpc<{ status: ContractStatus }>("contract_countersign", {
    p_contract: id, p_signature_data: signatureData, p_client: clientInfo(),
    p_signing_name: who.name?.trim() || null, p_signing_position: who.position?.trim() || null, p_authority: who.authority ?? null,
    p_confirm_name: !!who.confirmName, p_early: !!who.early,
  });

/** Datei als Anhang des Vertrags ablegen (Kündigungsschreiben, Scan einer Papierunterschrift); liefert den Speicherpfad. */
export async function uploadContractAttachment(api: Api, orgId: string, contractId: string, file: Blob, fileName: string, prefix: string): Promise<string> {
  const path = `${orgId}/contract/${contractId}/${Date.now()}-${prefix}-${safeFileName(fileName)}`;
  await api.upload("documents", path, file);
  await api.insert("attachments", { org_id: orgId, owner_kind: "contract", owner_id: contractId, storage_path: path,
    file_name: fileName.slice(0, 200) || "dokument.pdf", mime_type: file.type || "application/pdf", size_bytes: file.size }, { columns: "id" });
  return path;
}

/** Unterschrift einer Partei auf Papier nachtragen (Praxis): Scan als Anhang, danach wie eine Unterschrift in der Beweiskette. */
export async function recordPaperSignature(api: Api, orgId: string, contractId: string, signerId: string,
  input: { name: string; signedOn: string; position?: string | null; file: Blob; fileName: string }): Promise<{ status: ContractStatus }> {
  const path = await uploadContractAttachment(api, orgId, contractId, input.file, input.fileName, "papier");
  return api.rpc<{ status: ContractStatus }>("contract_record_paper_signature", {
    p_signer: signerId, p_signing_name: input.name.trim(), p_signed_on: input.signedOn, p_document_path: path, p_signing_position: input.position?.trim() || null,
  });
}

/** Abgelegtes PDF ersetzen (Befund 25): mit Grund; ohne neue Creator-Fassung bleibt die bisherige. */
export async function replaceFinalPdf(api: Api, orgId: string, id: string, file: Blob, fileName: string, talentFile: Blob | null, reason: string): Promise<void> {
  const stamp = Date.now();
  const path = `${orgId}/contract/${id}/${stamp}-${safeFileName(fileName)}`;
  await api.upload("documents", path, file);
  let talentPath: string | null = null;
  if (talentFile) {
    talentPath = `${orgId}/contract/${id}/${stamp}-creator-${safeFileName(fileName)}`;
    await api.upload("documents", talentPath, talentFile);
  }
  await api.rpc("contract_replace_final_pdf", { p_contract: id, p_path: path, p_path_talent: talentPath, p_reason: reason.trim() });
}

// Vorlagen
export const newTemplateVersion = (api: Api, id: string) => api.rpc<string>("contract_template_new_version", { p_template: id });
export const activateTemplate = (api: Api, id: string) => api.rpc("contract_template_activate", { p_template: id });

/**
 * Externen, bereits unterschriebenen Vertrag erfassen (fs207, Befund 14): Datensatz als Entwurf anlegen, PDF hochladen,
 * anhängen (erst dann gilt er als abgeschlossen). Scheitert das Hochladen, wird der Entwurf wieder gelöscht.
 */
export async function registerExternal(api: Api, orgId: string, input: {
  talentId: string | null; title: string; signedOn: string; language: Language; brandId: string | null; dealId: string | null; kindId: string | null;
  file: Blob; fileName: string; talentFile?: Blob | null; visibleToTalent?: boolean | null;
}): Promise<string> {
  const id = await api.rpc<string>("contract_register_external", {
    p_talent: input.talentId, p_title: input.title, p_signed_on: input.signedOn, p_language: input.language,
    p_brand: input.brandId, p_deal: input.dealId, p_kind: input.kindId, p_org: orgId, p_pending: true,
    p_visible_to_talent: input.visibleToTalent ?? null,
  });
  try {
    const safe = safeFileName(input.fileName);
    const stamp = Date.now();
    const path = `${orgId}/contract/${id}/${stamp}-${safe}`;
    await api.upload("documents", path, input.file);
    let talentPath: string | null = null;
    if (input.talentFile) {
      talentPath = `${orgId}/contract/${id}/${stamp}-creator-${safe}`;
      await api.upload("documents", talentPath, input.talentFile);
    }
    await api.rpc("contract_attach_external_pdf", { p_contract: id, p_path: path, p_path_talent: talentPath });
  } catch (e) {
    await deleteDraft(api, id).catch(() => undefined);
    throw e;
  }
  return id;
}
